/**
 * Native Platform Servisleri
 * 
 * Capacitor native özelliklerini (Haptics, StatusBar) merkezi olarak yönetir.
 * Web/PWA'da sessizce no-op olarak çalışır.
 */
import { Capacitor } from '@capacitor/core'
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics'
import { StatusBar, Style } from '@capacitor/status-bar'

const isNative = Capacitor.isNativePlatform()

// ─── Haptic Feedback ────────────────────────────────────────
export const haptic = {
  /** Hafif dokunmatik geri bildirim — buton tıklamaları */
  light: () => { if (isNative) Haptics.impact({ style: ImpactStyle.Light }).catch(() => {}) },

  /** Orta dokunmatik geri bildirim — önemli aksiyonlar */
  medium: () => { if (isNative) Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {}) },

  /** Ağır dokunmatik geri bildirim — kritik aksiyonlar */
  heavy: () => { if (isNative) Haptics.impact({ style: ImpactStyle.Heavy }).catch(() => {}) },

  /** Başarı bildirimi */
  success: () => { if (isNative) Haptics.notification({ type: NotificationType.Success }).catch(() => {}) },

  /** Hata bildirimi */
  error: () => { if (isNative) Haptics.notification({ type: NotificationType.Error }).catch(() => {}) },

  /** Uyarı bildirimi */
  warning: () => { if (isNative) Haptics.notification({ type: NotificationType.Warning }).catch(() => {}) },

  /** Seçim değişikliği (picker, toggle) */
  selection: () => { if (isNative) Haptics.selectionChanged().catch(() => {}) },
}

// ─── Status Bar Yönetimi ────────────────────────────────────
export const statusBar = {
  /** Koyu tema (açık arka plan → koyu ikonlar) */
  setLight: async () => {
    if (!isNative) return
    try {
      await StatusBar.setStyle({ style: Style.Light })
      if (Capacitor.getPlatform() !== 'android') {
        await StatusBar.setBackgroundColor({ color: '#FCFAF8' })
      }
    } catch { /* web'de sessiz */ }
  },

  /** Açık tema (koyu arka plan → açık ikonlar) */
  setDark: async () => {
    if (!isNative) return
    try {
      await StatusBar.setStyle({ style: Style.Dark })
      if (Capacitor.getPlatform() !== 'android') {
        await StatusBar.setBackgroundColor({ color: '#1A1A1A' })
      }
    } catch { /* web'de sessiz */ }
  },

  /** Status bar'ı göster */
  show: async () => {
    if (!isNative) return
    try { await StatusBar.show() } catch { /* web'de sessiz */ }
  },

  /** Status bar'ı gizle */
  hide: async () => {
    if (!isNative) return
    try { await StatusBar.hide() } catch { /* web'de sessiz */ }
  },
}

/**
 * Uygulama başlarken native servisleri yapılandırır.
 * App.tsx veya main.tsx'ten çağrılır.
 */
export async function initNativeServices(): Promise<void> {
  if (!isNative) return

  // Status bar'ı varsayılan temaya ayarla (açık arka plan → koyu ikonlar)
  await statusBar.setLight()
}
